// バリデーション関数 - 入力値検証とサニタイゼーション
import type {
  DeviceCondition,
  PlacementVerificationStatus,
} from "../../_root/packages/-components/DeviceInventoryOperation/type";
import {
  DEVICE_CONDITION,
  PLACEMENT_VERIFICATION_STATUS,
} from "../../_root/packages/-components/DeviceInventoryOperation/type";

/**
 * バリデーションエラークラス
 */
export class ValidationError extends Error {
  constructor(
    message: string,
    public field: string,
  ) {
    super(message);
    this.name = "ValidationError";
  }
}

/**
 * パターンIDの検証
 */
export function validatePatternId(patternId: unknown): string {
  if (typeof patternId !== "string") {
    throw new ValidationError("Pattern ID must be a string", "patternId");
  }

  if (patternId.length === 0) {
    throw new ValidationError("Pattern ID cannot be empty", "patternId");
  }

  return patternId;
}

/**
 * アンカー日付の検証
 */
export function validateAnchorDate(anchorDate: unknown): string {
  if (typeof anchorDate !== "string") {
    throw new ValidationError("Anchor date must be a string", "anchorDate");
  }

  if (anchorDate.length === 0) {
    throw new ValidationError("Anchor date cannot be empty", "anchorDate");
  }

  return anchorDate;
}

/**
 * リクエストIDの検証
 */
export function validateRequestId(requestId: unknown): string {
  if (typeof requestId !== "string") {
    throw new ValidationError("Request ID must be a string", "requestId");
  }

  if (requestId.length === 0) {
    throw new ValidationError("Request ID cannot be empty", "requestId");
  }

  return requestId;
}

/**
 * デバイスIDの検証
 */
export function validateDeviceId(deviceId: unknown): string {
  if (typeof deviceId !== "string") {
    throw new ValidationError("Device ID must be a string", "deviceId");
  }

  if (deviceId.length === 0) {
    throw new ValidationError("Device ID cannot be empty", "deviceId");
  }

  // 基本的なサニタイゼーション（危険な文字を除去）
  const sanitized = deviceId.replace(/[<>"'&]/g, "");
  if (sanitized !== deviceId) {
    throw new ValidationError(
      "Device ID contains invalid characters",
      "deviceId",
    );
  }

  return deviceId;
}

/**
 * デバイス状態の検証
 */
export function validateDeviceCondition(
  value: unknown,
): DeviceCondition | null {
  if (
    typeof value === "string" &&
    Object.values(DEVICE_CONDITION).includes(value as DeviceCondition)
  ) {
    return value as DeviceCondition;
  }
  return null;
}

/**
 * 配置確認状態の検証
 */
export function validatePlacementVerificationStatus(
  value: unknown,
): PlacementVerificationStatus | null {
  if (
    typeof value === "string" &&
    Object.values(PLACEMENT_VERIFICATION_STATUS).includes(
      value as PlacementVerificationStatus,
    )
  ) {
    return value as PlacementVerificationStatus;
  }
  return null;
}

/**
 * IDの安全な変換
 */
export function safeConvertToId(value: unknown): string {
  if (value == null) {
    throw new Error("ID cannot be null or undefined");
  }

  if (typeof value === "string") {
    if (value.length === 0) {
      throw new Error("ID cannot be empty string");
    }
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  // オブジェクトの場合は文字列化を試みる
  if (typeof value === "object") {
    const stringified = JSON.stringify(value);
    if (stringified === "{}") {
      throw new Error("ID cannot be empty object");
    }
    return stringified;
  }

  throw new Error(`Invalid ID type: ${typeof value}`);
}
